import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, Map, BadgeCheck, Users, GraduationCap, Megaphone, Truck, ShieldCheck, BarChart3, Check } from 'lucide-react';
import { MARKETS, GLOBAL_INFO, MASTER_BENEFITS, PROCESS_STEPS, BRAND } from '../data/mock';
import Globe from '../components/Globe';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { createEnquiry } from '../lib/api';

const iconMap = { Map, BadgeCheck, Users, GraduationCap, Megaphone, Truck, ShieldCheck, BarChart3 };

export default function GlobalExpansion() {
  const { toast } = useToast();
  const [selected, setSelected] = useState('India');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', phone:'', country:'', city:'', territory:'', format:'', budget:'', experience:'', space:'', source:'', message:'', consent:false });
  const onChange = (k, v) => setForm(f => ({...f, [k]: v}));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast({ title: 'Consent required', description: 'Please agree to be contacted.', variant: 'destructive' }); return; }
    if (!form.name || !form.email) { toast({ title: 'Missing details', description: 'Please add your name and email.' }); return; }
    setBusy(true);
    try {
      await createEnquiry({ ...form, type: 'global', country: form.country || selected });
      toast({ title: 'Enquiry submitted', description: 'Thanks! Our global team will reach out shortly.' });
      setForm({ name:'', email:'', phone:'', country:'', city:'', territory:'', format:'', budget:'', experience:'', space:'', source:'', message:'', consent:false });
    } catch (err) {
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-24">
      <Seo title="Global Expansion — Master Franchise Markets" path="/global-expansion" description="Zayka Sphere is going global. Explore master franchise opportunities across India, UAE, USA, Japan & more with our interactive market globe and expansion roadmap." />
      {/* HERO */}
      <section className="relative overflow-hidden ember-bg">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-[28rem] h-[28rem] bg-orange-500/15 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="text-center lg:text-left"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs uppercase tracking-[0.25em] mb-6">
              <Flame className="w-3.5 h-3.5" /> Global Expansion
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95]">Zayka Sphere <br /><span className="text-orange-500 fire-glow">Going Global</span></h1>
            <p className="mt-5 text-white/70 text-lg">From local flavours to global opportunities.</p>
            <div className="mt-8 flex flex-wrap gap-2 justify-center lg:justify-start max-w-2xl">
              {MARKETS.map(m => (
                <button
                  key={m}
                  data-testid={`market-btn-${m}`}
                  onClick={() => setSelected(m)}
                  className={`px-4 h-10 rounded-full text-sm font-semibold uppercase tracking-widest border transition-all duration-300 ${selected === m ? 'bg-orange-500 border-orange-500 text-white shadow-lg shadow-orange-500/40 scale-105' : 'border-white/15 text-white/70 hover:text-orange-400 hover:border-orange-500/50'}`}
                >{m}</button>
              ))}
            </div>
            <p className="mt-6 text-xs text-white/40 max-w-xl mx-auto lg:mx-0">Drag the globe to spin it, or tap a market to fly there. Markets shown are target expansion opportunities, not confirmed or guaranteed available territories.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.15 }}
            className="relative"
          >
            <Globe selected={selected} />
            <div className="mt-4 text-center">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/40 border border-orange-500/30 text-orange-300 text-sm font-semibold tracking-wide" data-testid="globe-selected-label">
                <Map className="w-4 h-4" /> {selected}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MASTER FRANCHISE */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Master Franchise</div>
            <h2 className="font-display text-4xl md:text-5xl">Global Master <span className="text-orange-500">Franchise Opportunity</span></h2>
            <p className="mt-3 text-white/60">A master franchise partner may receive territory-based rights, subject to agreement, to develop Zayka Sphere within a defined market.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 tilt-scene">
            {MASTER_BENEFITS.map((b, i) => {
              const Icon = iconMap[b.icon];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                >
                  <Card className="food-card relative overflow-hidden bg-neutral-900 border border-white/10 rounded-2xl p-6 h-full will-change-transform">
                    <span className="tilt-glare" />
                    <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/40 flex items-center justify-center text-orange-500"><Icon className="w-6 h-6" /></div>
                    <h3 className="mt-4 font-bebas text-xl">{b.title}</h3>
                    <p className="mt-1 text-sm text-white/60">{b.desc}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INFO */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Information</div>
            <h2 className="font-display text-4xl md:text-5xl">Global Investment <span className="text-orange-500">Information</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 tilt-scene">
            {GLOBAL_INFO.map((g, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.1 }}
              >
                <Card className="food-card relative overflow-hidden bg-neutral-900 border border-white/10 rounded-2xl p-7 h-full will-change-transform">
                  <span className="tilt-glare" />
                  <div className="text-orange-500 text-xs uppercase tracking-widest">{g.label}</div>
                  <div className="mt-2 font-display text-3xl md:text-4xl text-white">{g.value}</div>
                  <div className="mt-3 text-xs text-white/40">Illustrative / Estimated — subject to territory, unit format, location, agreement and market conditions.</div>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-xs text-white/40 text-center max-w-3xl mx-auto">Figures shown are illustrative ranges from supplied material. Zayka Sphere makes no claim or representation of guaranteed returns, revenue or profitability.</p>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Roadmap</div>
            <h2 className="font-display text-4xl md:text-5xl">Franchise <span className="text-orange-500">Process</span></h2>
          </div>
          <div className="relative">
            {/* glowing connector line */}
            <div className="hidden lg:block absolute top-9 left-[10%] right-[10%] h-[3px] roadmap-line rounded-full" />
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 relative">
              {PROCESS_STEPS.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="floaty w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center font-display text-2xl text-white shadow-lg shadow-orange-500/40 ring-4 ring-black relative z-10" style={{ animationDelay: `${i * 0.4}s` }}>{p.no}</div>
                    <div className="tilt-card food-card mt-4 w-full rounded-2xl border border-white/10 bg-neutral-900 p-6 will-change-transform relative overflow-hidden">
                      <span className="tilt-glare" />
                      <h4 className="font-bebas text-xl">{p.title}</h4>
                      <p className="mt-2 text-sm text-white/55">{p.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section id="enquiry" className="py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Franchise Enquiry</div>
            <h2 className="font-display text-4xl md:text-5xl">Ready To Build Zayka Sphere <span className="text-orange-500">In Your Market?</span></h2>
            <p className="mt-3 text-white/60">Share a few details and our franchise team will get in touch.</p>
          </div>
          <form onSubmit={onSubmit} className="bg-neutral-900 border border-white/10 rounded-3xl p-6 md:p-10 grid md:grid-cols-2 gap-5">
            <Input placeholder="Full Name" value={form.name} onChange={e=>onChange('name', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Email" type="email" value={form.email} onChange={e=>onChange('email', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Phone / WhatsApp" value={form.phone} onChange={e=>onChange('phone', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Country" value={form.country} onChange={e=>onChange('country', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="City" value={form.city} onChange={e=>onChange('city', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Preferred Territory" value={form.territory} onChange={e=>onChange('territory', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Select value={form.format} onValueChange={v=>onChange('format', v)}>
              <SelectTrigger className="h-12 bg-black/50 border-white/10"><SelectValue placeholder="Preferred Franchise Format" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="express">Express / Cafe Format</SelectItem>
                <SelectItem value="standard">Standard Dine-In Restaurant</SelectItem>
                <SelectItem value="premium">Premium Lounge Restaurant</SelectItem>
                <SelectItem value="master">Master Franchise</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Estimated Investment Budget" value={form.budget} onChange={e=>onChange('budget', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Business Experience" value={form.experience} onChange={e=>onChange('experience', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Available Space" value={form.space} onChange={e=>onChange('space', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="How did you hear about us?" value={form.source} onChange={e=>onChange('source', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Textarea placeholder="Message" value={form.message} onChange={e=>onChange('message', e.target.value)} className="md:col-span-2 min-h-[110px] bg-black/50 border-white/10" />
            <label className="md:col-span-2 flex items-start gap-3 text-sm text-white/70">
              <Checkbox checked={form.consent} onCheckedChange={v=>onChange('consent', !!v)} className="mt-0.5 border-orange-500 data-[state=checked]:bg-orange-500" />
              <span>I agree to be contacted regarding Zayka Sphere franchise opportunities.</span>
            </label>
            <Button type="submit" disabled={busy} className="md:col-span-2 h-12 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><Check className="w-4 h-4 mr-2" /> {busy ? 'Submitting…' : 'Submit Franchise Enquiry'}</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
